export default class AxDateUtils {
  public static MONTHS: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  public static DAYS_OF_WEEK: string[] = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  public static isLeapYear(year: number) {
    if (Number(year)) {
      return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    }
  }

  // Returns a two-digit number if the number is single-digit
  public static setTwoDigits(numericValue: number){
    if (Number(numericValue) < 10) {
      return '0' + numericValue.toString();
    } else {
      return numericValue.toString();
    }
  }

  public static makeUTCDate(...args: number[]): Date {
    const _args = Array.from(arguments) as [
      number,
      number,
      number?,
      number?,
      number?,
      number?,
      number?
    ];

    if (_args.length > 7) {
      throw new Error('Invalid number of arguments for Date constructor');
    }

    const tmp = new Date(..._args);
    const offset = tmp.getTimezoneOffset() * 60000;

    return new Date(tmp.getTime() - offset);
  }

  public static checkDate(option: string, year: number, month: number, calendarDate: number){
    const leapYearMonthCheckOptions = [
      'calendarGrid',
      'monthStep',
      'yearStep',
      'monthDropdownSelect',
      'yearDropdownSelect',
    ];
    let isInvalidDate = false;

    year = Number(year);

    if (year.toString().length <= 2) {
      year = 2000 + year;
    }

    month = Number(month);
    if(month < 0 || month > 11) {
      isInvalidDate = true;
    }

    calendarDate = Number(calendarDate);

    // Only adjust for 29th Feb in a non leap year. Anything else will be flagged as invalid
    const checkLeapYearStrict = () => {
      if(month=== 1){
        if(!AxDateUtils.isLeapYear(year) && calendarDate === 29){
          calendarDate = 28;
        }
        if(calendarDate > 29){
          isInvalidDate = true;
        }
      }
    };

    // Anything above 28th in a non lep year will be adjusted to 28 Feb,
    //  and Anything > 29th Feb in a leap year is adjusted to 29th Feb.
    // This is for Year and Month steppers
    const adjustLeapYear = () => {
      if (month=== 1) {
        // Non leap year
        if (!AxDateUtils.isLeapYear(year) && calendarDate > 28) {
          calendarDate = 28;
        }
        // Leap year
        if (AxDateUtils.isLeapYear(year) && calendarDate > 29) {
          calendarDate = 29;
        }
      }
    };

    const adjust30DayMonth = () => {
      if ([3, 5, 8, 10].includes(month)) {
        if (calendarDate === 31) {
          calendarDate = 30;
        }
        if (calendarDate > 30) {
          isInvalidDate = true;
        }
      }
    };

    const adjust30DayMonthStrict = () => {
      if ([3, 5, 8, 10].includes(month)) {
        if (calendarDate > 30) {
          isInvalidDate = true;
        }
      }
    };

    const adjust31DayMonthStrict = () => {
      if ([0, 2, 4, 6, 7, 9, 11].includes(month)) {
        if (calendarDate > 31) {
          isInvalidDate = true;
        }
      }
    };

    if(option === "dateInputField"){
      checkLeapYearStrict(); // Check for 29th Feb only
      adjust30DayMonthStrict(); // Check 30-day month
      adjust31DayMonthStrict(); // Check 31-day month

      // Invalid days
      if (isInvalidDate) {
        console.error("error: invalid date");
        return null;
      }
    }

    if (leapYearMonthCheckOptions.some(optionType => option === optionType)) {
      adjustLeapYear();
      adjust30DayMonth();
    }

    return AxDateUtils.makeUTCDate(year, month, calendarDate);
  }

  public static getDayName(day: number) {
    return(AxDateUtils.DAYS_OF_WEEK[day])
  }

  public static getMonthName(month: number){
    return AxDateUtils.MONTHS[month];
  }

  public static getQuarterDates(year: number, quarter: number){
    let startDate, endDate;

    if (quarter === 1) {
      startDate = AxDateUtils.makeUTCDate(year, 6, 1);
      endDate = AxDateUtils.makeUTCDate(year, 8, 30);
    }

    if (quarter === 2) {
      startDate = AxDateUtils.makeUTCDate(year, 9, 1);
      endDate = AxDateUtils.makeUTCDate(year, 111, 31);
    }

    if(quarter === 3){
      startDate = AxDateUtils.makeUTCDate(year, 0, 1);
      endDate = AxDateUtils.makeUTCDate(year, 2, 31);
    }

    if(quarter === 4){
      startDate = AxDateUtils.makeUTCDate(year, 3, 1);
      endDate = AxDateUtils.makeUTCDate(year, 5, 30);
    }

    return [startDate, endDate];
  }

  public static getisInvalidDate() {
    return this.getisInvalidDate;
  }

  // Returns date in a preferred format
  public static getFormattedDate(type: string, chosenDate: Date){
    const selectedDay = chosenDate.getUTCDay(); // Day of week - Sunday gives 0
    const selectedDate = chosenDate.getUTCDate();
    const selectedMonth = chosenDate.getUTCMonth(); // January gives 0
    const selectedYear = chosenDate.getUTCFullYear();
    const selectedDateddmmyyyy =
      AxDateUtils.setTwoDigits(selectedDate) +
      ' / ' +
      AxDateUtils.setTwoDigits(selectedMonth + 1) +
      ' / ' +
      selectedYear;
    const selectedDateFull =
      this.getDayName(selectedDay) +
      ', ' +
      selectedDate +
      ' / ' +
      (selectedMonth + 1) +
      ' / ' +
      selectedYear;

    switch (type) {
      case 'full':
        return selectedDateFull;
      case 'ddmmyyyy':
        return selectedDateddmmyyyy;
      case 'date':
        return selectedDate;
      case 'month':
        return selectedMonth;
      case 'monthName':
        return this.getMonthName(selectedMonth);
      case 'year':
        return selectedYear;
      default:
        return selectedDateFull;
    }
  }

  // For AxDatepicker
  // Calendar for AxDatepicker
  // Returns an array of all the dates of a calendar month,
  //   including backfill for previous month and next month
  public static getWholeMonth(month: number, year: number) {
    const wholeMonth = [];

    const firstDay = AxDateUtils.makeUTCDate(year, month, 1);
    const lastDay = AxDateUtils.makeUTCDate(year, month + 1, 0);

    // Backfill previous month days
    for (let day = 0; day > -1 * firstDay.getUTCDay(); day--) {
      wholeMonth.unshift(AxDateUtils.makeUTCDate(year, month, day));
    }

    // Fill with current month
    for (let day = firstDay.getUTCDate(); day <= lastDay.getUTCDate(); day++) {
      wholeMonth.push(AxDateUtils.makeUTCDate(year, month, day));
    }

    // Pad with following month days
    // Calendar requires 6 rows, meaning 6*7=42 cells in the grid
    //   6 for rest of the week, 13 for additional week
    for (let day = 1; day <= 13 - lastDay.getUTCDay(); day++) {
      wholeMonth.push(AxDateUtils.makeUTCDate(year, month + 1, day));
      if (wholeMonth.length >= 42) {
        break;
      }
    }
    return wholeMonth;
  }

  public static createCalendarBody(workingDate: Date){
    const wholeMonth = AxDateUtils.getWholeMonth(
      workingDate.getUTCMonth(),
      workingDate.getUTCFullYear()
    );
    let calendar = document.createElement('div');
    calendar.classList.add('ax-date-picker-calendar');
    calendar = AxDateUtils.buildCalendarHeader(calendar);
    calendar = AxDateUtils.createCalendarGrid(calendar, wholeMonth, workingDate);

    return calendar;
  }

  private static buildCalendarHeader(calendarBody: HTMLDivElement){
    const body = calendarBody;
    const days = AxDateUtils.DAYS_OF_WEEK;
    days.forEach((day)=> {
      const cell = document.createElement('div');
      cell.classList.add('ax-date-picker-calendar__header-cell');
      cell.innerHTML += day.slice(0,2);
      body.appendChild(cell);
    })
    return body;
  }

  private static createCalendarGrid(
    calendarBody: HTMLDivElement,
    wholeMonth: Date[],
    workingDate: Date){
    let currentMonth: number;
    // If first day in the array it not a Sunday, it must belong to the previous month
    if (wholeMonth[0].getUTCDate() !== 1) {
      // If first cell is December
      if (wholeMonth[0].getUTCMonth() === 11) {
        currentMonth = 0;
      } else {
        currentMonth = wholeMonth[0].getUTCMonth() + 1;
      }
    } else {
      currentMonth = wholeMonth[0].getUTCMonth();
    }

    const referenceDate = AxDateUtils.makeUTCDate(
      workingDate.getUTCFullYear(),
      workingDate.getUTCMonth(),
      workingDate.getUTCDate()
    ).toUTCString();

    wholeMonth.forEach((day: Date) => {
      const cell = document.createElement('div');
      cell.classList.add('ax-date-picker-calendar__content-cell');

      // Feb - Nov
      if(currentMonth !== 0 && currentMonth !== 11){
        if(day.getUTCMonth() < currentMonth){
          cell.classList.add(
            'ax-date-picker-calendar__content-cell--previous-month'
          );
        }
        if(day.getUTCMonth() > currentMonth){
          cell.classList.add(
            'ax-date-picker-calendar__content-cell--next-month'
          );
        }
      }
      // Jan
      if(currentMonth === 0){
        if(day.getUTCMonth() > currentMonth && day.getUTCMonth() !== 11){
          cell.classList.add(
            'ax-date-picker-calendar__content-cell--next-month'
          );
        }
        if(day.getUTCMonth() > currentMonth && day.getUTCMonth() === 11){
          cell.classList.add(
            'ax-date-picker-calendar__content-cell--previous-month'
          );
        }
      }
      // Dec
      if(currentMonth === 11){
        if(day.getUTCMonth() < currentMonth && day.getUTCMonth() !== 0){
          cell.classList.add(
            'ax-date-picker-calendar__content-cell--previous-month'
          );
        }
        if(day.getUTCMonth() < currentMonth && day.getUTCMonth() === 0){
          cell.classList.add(
            'ax-date-picker-calendar__content-cell--next-month'
          );
        }
      }

      const thisDay = AxDateUtils.makeUTCDate(
        day.getUTCFullYear(),
        day.getUTCMonth(),
        day.getUTCDate()
      ).toUTCString();
      if (thisDay === referenceDate) {
        cell.classList.add(
          'ax-date-picker-calendar__content-cell--current-date'
        );
      }
      cell.innerHTML += AxDateUtils.setTwoDigits(day.getUTCDate());
      calendarBody.appendChild(cell);
    });

    return calendarBody;
  }
}
